/**
 * "משימה חדשה" remembers the project the user last created a task in
 * (round 2, 2026-10-11): QuickCreate opens the task form on that project when
 * the screen gives no project of its own. The screen's project always wins.
 *
 * One key per user in localStorage. Storage can be missing or throw (private
 * mode, blocked site data), so every read and write is guarded and a failure
 * only means "nothing remembered".
 */

export interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

const PREFIX = 'ote-last-task-project:';
/** Ids are UUIDs; anything else in the slot is ignored. */
const ID_RE = /^[A-Za-z0-9-]{1,64}$/;

function browserStore(): KeyValueStore | null {
  try {
    return typeof window !== 'undefined' && window.localStorage ? window.localStorage : null;
  } catch {
    return null;
  }
}

export function rememberTaskProject(userId: string | undefined, projectId: string | undefined, store: KeyValueStore | null = browserStore()): void {
  if (!userId || !projectId || !store || !ID_RE.test(projectId)) return;
  try {
    store.setItem(PREFIX + userId, projectId);
  } catch {
    /* storage full or blocked: nothing remembered */
  }
}

export function recallTaskProject(userId: string | undefined, store: KeyValueStore | null = browserStore()): string | undefined {
  if (!userId || !store) return undefined;
  try {
    const v = store.getItem(PREFIX + userId);
    return v && ID_RE.test(v) ? v : undefined;
  } catch {
    return undefined;
  }
}

/** The project "משימה חדשה" opens on: the screen's project, else the remembered one. */
export function taskProjectFor(contextProjectId: string | undefined, userId: string | undefined, store: KeyValueStore | null = browserStore()): string | undefined {
  return contextProjectId || recallTaskProject(userId, store);
}

// ── P-02b (plan §6.1): the last ENGAGEMENT replaces the last project ─────────
// A task now lives in its engagement (D-26). "משימה חדשה" from QuickCreate with
// nothing on screen opens on the engagement the user last created a task in;
// the form reads its customer and type from it. Same guarded storage.

const ENGAGEMENT_PREFIX = 'ote-last-task-engagement:';

export function rememberTaskEngagement(userId: string | undefined, engagementId: string | undefined, store: KeyValueStore | null = browserStore()): void {
  if (!userId || !engagementId || !store || !ID_RE.test(engagementId)) return;
  try {
    store.setItem(ENGAGEMENT_PREFIX + userId, engagementId);
  } catch {
    /* storage full or blocked: nothing remembered */
  }
}
